import { useAuthStore } from "../stores/authStore";
import { useFyStore } from "../stores/fyStore";

type CacheScope = "tenant" | "tenant-fy" | "global";
type StorageMode = "memory" | "local";

type CacheEntry<T> = {
  data: T;
  expiresAt: number | null;
  tags: string[];
};

type CachedQueryOptions<T> = {
  key: string;
  tags: string[];
  fetcher: () => Promise<T>;
  ttlMs?: number | null;
  scope?: CacheScope;
  storage?: StorageMode;
  forceRefresh?: boolean;
  revalidateCached?: (data: T) => boolean;
};

const STORAGE_PREFIX = "transporterp:master-cache:";

const memoryCache = new Map<string, CacheEntry<unknown>>();
const inflightRequests = new Map<string, Promise<unknown>>();
const tagIndex = new Map<string, Set<string>>();
const tagGenerations = new Map<string, number>();
const keyGenerations = new Map<string, number>();

function isBrowser() {
  return typeof window !== "undefined" && typeof window.localStorage !== "undefined";
}

function getScopePrefix(scope: CacheScope) {
  if (scope === "global") {
    return "global";
  }

  const tenantId = useAuthStore.getState().tenantId ?? "tenant-1";
  if (scope === "tenant") {
    return `tenant:${tenantId}`;
  }

  const fyCode = useFyStore.getState().activeFyCode ?? "fy-default";
  return `tenant:${tenantId}:fy:${fyCode}`;
}

function buildScopedKey(scope: CacheScope, key: string) {
  return `${getScopePrefix(scope)}:${key}`;
}

function readLocalEntry<T>(scopedKey: string) {
  if (!isBrowser()) return null;

  try {
    const raw = window.localStorage.getItem(`${STORAGE_PREFIX}${scopedKey}`);
    if (!raw) return null;

    const parsed = JSON.parse(raw) as CacheEntry<T>;
    if (!parsed || (parsed.expiresAt !== null && typeof parsed.expiresAt !== "number")) return null;

    if (parsed.expiresAt !== null && Date.now() >= parsed.expiresAt) {
      window.localStorage.removeItem(`${STORAGE_PREFIX}${scopedKey}`);
      return null;
    }

    return parsed;
  } catch {
    return null;
  }
}

function writeLocalEntry<T>(scopedKey: string, entry: CacheEntry<T>) {
  if (!isBrowser()) return;

  try {
    window.localStorage.setItem(`${STORAGE_PREFIX}${scopedKey}`, JSON.stringify(entry));
  } catch {
    // Ignore storage quota or serialization failures; memory cache still works.
  }
}

function deleteLocalEntry(scopedKey: string) {
  if (!isBrowser()) return;
  window.localStorage.removeItem(`${STORAGE_PREFIX}${scopedKey}`);
}

function findLocalKeysByTags(tags: string[]) {
  if (!isBrowser()) return [];

  const tagSet = new Set(tags);
  const keys: string[] = [];

  for (let index = 0; index < window.localStorage.length; index += 1) {
    const storageKey = window.localStorage.key(index);
    if (!storageKey?.startsWith(STORAGE_PREFIX)) continue;

    try {
      const raw = window.localStorage.getItem(storageKey);
      if (!raw) continue;

      const parsed = JSON.parse(raw) as CacheEntry<unknown>;
      if (!Array.isArray(parsed.tags)) continue;

      if (parsed.tags.some((tag) => tagSet.has(tag))) {
        keys.push(storageKey.slice(STORAGE_PREFIX.length));
      }
    } catch {
      // Ignore malformed local cache entries; they are harmless and will be
      // replaced by the next successful fetch for their key.
    }
  }

  return keys;
}

function registerTags(scopedKey: string, tags: string[]) {
  tags.forEach((tag) => {
    const existing = tagIndex.get(tag) ?? new Set<string>();
    existing.add(scopedKey);
    tagIndex.set(tag, existing);
  });
}

function unregisterKey(scopedKey: string) {
  for (const [tag, keys] of tagIndex.entries()) {
    if (!keys.delete(scopedKey)) continue;
    if (keys.size === 0) {
      tagIndex.delete(tag);
    }
  }
}

function storeEntry<T>(scopedKey: string, entry: CacheEntry<T>, storage: StorageMode) {
  unregisterKey(scopedKey);
  memoryCache.set(scopedKey, entry as CacheEntry<unknown>);
  registerTags(scopedKey, entry.tags);

  if (storage === "local") {
    writeLocalEntry(scopedKey, entry);
  }
}

function readEntry<T>(scopedKey: string, storage: StorageMode) {
  const memoryEntry = memoryCache.get(scopedKey) as CacheEntry<T> | undefined;
  if (memoryEntry && (memoryEntry.expiresAt === null || Date.now() < memoryEntry.expiresAt)) {
    return memoryEntry;
  }

  if (memoryEntry) {
    unregisterKey(scopedKey);
    memoryCache.delete(scopedKey);
  }

  if (storage !== "local") {
    return null;
  }

  const localEntry = readLocalEntry<T>(scopedKey);
  if (!localEntry) {
    return null;
  }

  memoryCache.set(scopedKey, localEntry as CacheEntry<unknown>);
  registerTags(scopedKey, localEntry.tags);
  return localEntry;
}

function deleteEntry(scopedKey: string) {
  keyGenerations.set(scopedKey, (keyGenerations.get(scopedKey) ?? 0) + 1);
  memoryCache.delete(scopedKey);
  deleteLocalEntry(scopedKey);
  unregisterKey(scopedKey);
}

/**
 * Reusable master-data cache.
 * Future reference:
 * 1. Use tagged keys so mutations can invalidate related lookup/list entries together.
 * 2. Scope cache entries by tenant or tenant+financial-year to avoid cross-context leaks.
 * 3. Dedupe inflight requests so repeated UI opens do not fan out duplicate API calls.
 */
export async function getCachedMasterData<T>({
  key,
  tags,
  fetcher,
  ttlMs = null,
  scope = "tenant",
  storage = "local",
  forceRefresh = false,
  revalidateCached
}: CachedQueryOptions<T>) {
  const scopedKey = buildScopedKey(scope, key);
  if (forceRefresh) {
    // A manual refresh replaces only this query. Related cached master lists
    // remain hot until a master-data mutation invalidates their shared tags.
    deleteEntry(scopedKey);
  }

  if (!forceRefresh) {
    const cached = readEntry<T>(scopedKey, storage);
    if (cached && !revalidateCached?.(cached.data)) {
      return cached.data;
    }
    if (cached) deleteEntry(scopedKey);
  }

  const existingRequest = inflightRequests.get(scopedKey) as Promise<T> | undefined;
  if (existingRequest && !forceRefresh) {
    return existingRequest;
  }

  const requestGenerations = tags.map((tag) => tagGenerations.get(tag) ?? 0);
  const requestKeyGeneration = keyGenerations.get(scopedKey) ?? 0;
  const request = (async () => {
    const data = await fetcher();
    const isCurrent = (keyGenerations.get(scopedKey) ?? 0) === requestKeyGeneration
      && tags.every((tag, index) => (tagGenerations.get(tag) ?? 0) === requestGenerations[index]);
    if (isCurrent) {
      storeEntry(scopedKey, {
        data,
        expiresAt: ttlMs === null ? null : Date.now() + ttlMs,
        tags
      }, storage);
    }
    return data;
  })();

  inflightRequests.set(scopedKey, request as Promise<unknown>);

  try {
    return await request;
  } finally {
    if (inflightRequests.get(scopedKey) === request) {
      inflightRequests.delete(scopedKey);
    }
  }
}

export function invalidateMasterDataTags(tags: string[]) {
  const keysToClear = new Set<string>();

  tags.forEach((tag) => {
    tagGenerations.set(tag, (tagGenerations.get(tag) ?? 0) + 1);
    const keys = tagIndex.get(tag);
    if (!keys) return;
    keys.forEach((key) => keysToClear.add(key));
  });

  findLocalKeysByTags(tags).forEach((key) => keysToClear.add(key));

  keysToClear.forEach((key) => {
    deleteEntry(key);
  });
}

export function buildMasterDataKey(name: string, params?: Record<string, unknown>) {
  if (!params || Object.keys(params).length === 0) {
    return name;
  }

  const normalized = Object.entries(params)
    .sort(([left], [right]) => left.localeCompare(right))
    .map(([paramKey, value]) => [paramKey, value ?? null]);

  return `${name}:${JSON.stringify(normalized)}`;
}
